import { Link } from "react-router-dom";

import { StatusBadge, statusColor } from "@/components/lims";
import { SampleQrInline } from "@/components/SampleQr";

export const rowLabels = (rows) =>
  Array.from({ length: rows }, (_, i) => String.fromCharCode(65 + i));

/** `{ A1: sample }`, so a well can find its occupant in one lookup. */
export function occupancyMap(samples = []) {
  const byPosition = {};
  samples.forEach((sample) => {
    if (sample.position) byPosition[sample.position] = sample;
  });
  return byPosition;
}

export function boxCapacity(box) {
  return (box?.rows || 9) * (box?.cols || 9);
}

/**
 * The physical box, as a grid you can actually use.
 *
 * Every well was a `div` with `cursor-default` and a `title` attribute: the
 * grid looked interactive, was not, and could not be reached from a keyboard at
 * all. Each well is now a button carrying its own label, so the position is
 * selectable by click or by arrow-and-enter, and what is in it appears beside
 * the grid rather than in a tooltip that a touch device never shows.
 */
export default function BoxGrid({ box, selected, onSelect }) {
  const rows = box.rows || 9;
  const cols = box.cols || 9;
  const occupied = occupancyMap(box.samples);

  return (
    <div className="overflow-x-auto">
      <div
        role="grid"
        aria-label={`${box.name} positions`}
        className="inline-grid gap-1"
        style={{ gridTemplateColumns: `auto repeat(${cols}, minmax(0,1fr))` }}
      >
        <div aria-hidden="true" />
        {Array.from({ length: cols }, (_, c) => (
          <div key={`col-${c}`} aria-hidden="true" className="text-center text-[10px] text-meta">
            {c + 1}
          </div>
        ))}

        {rowLabels(rows).map((label) => (
          // A keyed fragment is required here. `<>` cannot take a key, so the
          // previous version put it on the inner child and React rebuilt the
          // whole row on every render.
          <div key={label} className="contents">
            <div aria-hidden="true" className="flex items-center pr-1 text-[10px] text-meta">
              {label}
            </div>
            {Array.from({ length: cols }, (_, c) => {
              const position = `${label}${c + 1}`;
              const sample = occupied[position];
              const isSelected = selected === position;
              return (
                <button
                  key={position}
                  type="button"
                  role="gridcell"
                  data-testid={`well-${position}`}
                  aria-label={
                    sample
                      ? `${position}, ${sample.sample_code}, ${sample.status}`
                      : `${position}, empty`
                  }
                  aria-pressed={isSelected}
                  onClick={() => onSelect(position)}
                  style={sample ? { backgroundColor: statusColor(sample.status) } : undefined}
                  className={`h-7 w-7 rounded text-[9px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 ${
                    sample ? "text-white hover:opacity-80" : "border border-line bg-surface hover:bg-slate-200"
                  } ${isSelected ? "ring-2 ring-teal-600 ring-offset-1 scale-110" : ""}`}
                >
                  {sample ? "" : ""}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * What is in the selected well.
 *
 * The grid used to sit above an inert list of names, so answering "what is in
 * C5" meant reading a tooltip and then finding that code in the list yourself.
 */
export function WellPreview({ box, position, onClear }) {
  if (!position) {
    return (
      <div className="rounded-lg border border-dashed border-line p-6 text-center text-sm text-meta">
        Select a position to see what is stored there.
      </div>
    );
  }

  const sample = occupancyMap(box.samples)[position];

  if (!sample) {
    return (
      <div className="rounded-lg border border-line p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-mono text-sm font-semibold text-ink">{position}</div>
            <p className="mt-1 text-sm text-meta">This position is free.</p>
          </div>
          <button type="button" onClick={onClear} className="text-xs text-meta hover:text-ink">
            Clear
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="well-preview" className="rounded-lg border border-line p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-sm font-semibold text-ink">{position}</div>
          <Link
            to={`/samples/${sample.id}`}
            className="mt-1 block truncate text-sm font-medium text-teal-800 hover:underline"
          >
            {sample.sample_code}
          </Link>
        </div>
        <StatusBadge status={sample.status} />
      </div>

      <dl className="mt-3 space-y-1.5 text-sm">
        {[
          ["Name", sample.name],
          ["Client", sample.client_name],
          ["Type", sample.sample_type],
          ["Received", sample.received_date],
        ].map(([term, value]) => (
          <div key={term} className="flex justify-between gap-3">
            <dt className="text-meta">{term}</dt>
            <dd className="min-w-0 truncate text-right text-ink">{value || "not recorded"}</dd>
          </div>
        ))}
      </dl>

      {/* Rendered rather than hidden behind a click, and rendered big: this
          panel is already about one specimen, and somebody standing at the
          freezer with the box open is holding a phone, not looking for an
          affordance. Large enough to scan off the screen without opening
          anything; tapping still enlarges it further. */}
      <div className="mt-4 flex flex-col items-center gap-2 border-t border-line pt-4">
        <SampleQrInline sampleId={sample.id} sampleCode={sample.sample_code} size="h-40 w-40" />
        <p className="text-center text-xs text-meta">
          Scan to open {sample.sample_code} on a phone or tablet.
        </p>
      </div>
    </div>
  );
}
